import { shallowRef, computed, toValue, ref, watch, nextTick, reactive, onBeforeMount, watchEffect, onBeforeUnmount, unref, useId, toRef, onMounted } from "vue";
const createBuilder = (builder) => builder;
function createElRef() {
  const elementRef = shallowRef(null);
  return computed({
    set: (ref2) => {
      const element = Array.isArray(ref2) ? ref2[0] : ref2;
      elementRef.value = getNativeElement(element);
    },
    get: () => elementRef.value
  });
}
const getNativeElement = (element) => {
  if (element && typeof element === "object" && "$el" in element) {
    return element.$el;
  }
  return element ?? null;
};
function getISOWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 864e5 + 1) / 7);
  return weekNo;
}
function isInDateRange(date, start, end) {
  start = new Date(start);
  start.setHours(0, 0, 0, 0);
  end = new Date(end);
  end.setHours(23, 59, 59, 999);
  const time = date.getTime();
  return time >= start.getTime() && time <= end.getTime();
}
function getNormalizedDayIndex(date, weekStartDay) {
  const day = date.getDay();
  const start = WEEKDAYS.indexOf(weekStartDay);
  const normalizedDay = day === 0 ? 6 : day - 1;
  return (normalizedDay - start + 7) % 7;
}
const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];
function sortDateRange(range) {
  const start = new Date(range.start);
  const end = range.end ? new Date(range.end) : void 0;
  if (end && end.getTime() < start.getTime()) {
    return { start: end, end: start };
  }
  return { start, end };
}
const _unstableCreateCalendar = createBuilder((options) => {
  const viewMonth = computed({
    get: () => {
      const date = toValue(options.viewMonth);
      return date ? new Date(date) : /* @__PURE__ */ new Date();
    },
    set: (newValue) => options.onUpdateViewMonth?.(new Date(newValue))
  });
  const weekdayNames = computed(() => {
    const formatter = new Intl.DateTimeFormat(toValue(options.locale), {
      weekday: toValue(options.calendarSize) === "big" ? "long" : "short"
    });
    const names = Array.from({ length: 7 }, (_, i) => new Date(2024, 0, 1 + i)).map(
      (day) => formatter.format(day)
    );
    const weekStartDay = toValue(options.weekStartDay);
    const index = WEEKDAYS.indexOf(weekStartDay);
    return names.slice(index).concat(names.slice(0, index));
  });
  const initialFocusedDate = () => {
    const today = /* @__PURE__ */ new Date();
    const view = viewMonth.value;
    const isTodayInViewMonth = today.getFullYear() === view.getFullYear() && today.getMonth() === view.getMonth();
    if (isTodayInViewMonth) {
      return today;
    } else {
      return new Date(view.getFullYear(), view.getMonth(), 1);
    }
  };
  const focusedDate = ref(initialFocusedDate());
  watch(
    () => toValue(options.modelValue),
    (newValue) => {
      if (!newValue) return;
      let newFocusDate;
      if (Array.isArray(newValue)) {
        newFocusDate = newValue.length ? new Date(newValue[0]) : void 0;
      } else if (typeof newValue === "object" && !(newValue instanceof Date)) {
        newFocusDate = new Date(newValue.start);
      } else {
        newFocusDate = new Date(newValue);
      }
      if (newFocusDate) {
        focusedDate.value = newFocusDate;
        viewMonth.value = newFocusDate;
      }
    },
    { immediate: true }
  );
  const isToday = (date) => {
    return date.toDateString() === (/* @__PURE__ */ new Date()).toDateString();
  };
  const isSelected = computed(() => {
    return (date) => {
      const value = toValue(options.modelValue);
      if (!value) return false;
      if (Array.isArray(value)) {
        const values = value.map((i) => new Date(i));
        return values.some((d) => d.toDateString() === date.toDateString());
      }
      if (typeof value === "object" && !(value instanceof Date)) {
        const start = new Date(value.start);
        const end = value.end ? new Date(value.end) : void 0;
        return start.toDateString() === date.toDateString() || end?.toDateString() === date.toDateString();
      }
      return new Date(value).toDateString() === date.toDateString();
    };
  });
  const isFocused = computed(() => {
    return (date) => {
      return focusedDate.value?.toDateString() === date.toDateString();
    };
  });
  const isDisabled = computed(() => {
    return (date) => {
      const disabledPropValue = toValue(options.disabled);
      if (typeof disabledPropValue === "boolean") {
        if (disabledPropValue) return true;
      }
      if (typeof disabledPropValue === "function") {
        if (disabledPropValue(date)) return true;
      }
      const min = toValue(options.min);
      const minDate = min ? new Date(min) : void 0;
      minDate?.setHours(0, 0, 0, 0);
      const max = toValue(options.max);
      const maxDate = max ? new Date(max) : void 0;
      maxDate?.setHours(23, 59, 59, 999);
      if (minDate && maxDate) return !isInDateRange(date, minDate, maxDate);
      if (minDate) return date.getTime() < minDate.getTime();
      if (maxDate) return date.getTime() > maxDate.getTime();
      return false;
    };
  });
  const weeksToRender = computed(() => {
    const weekStartDay = toValue(options.weekStartDay);
    const firstDayInViewMonth = new Date(
      viewMonth.value.getFullYear(),
      viewMonth.value.getMonth(),
      1
    );
    const startOffset = (firstDayInViewMonth.getDay() + 6) % 7;
    const daysBeforeStart = (startOffset - WEEKDAYS.indexOf(weekStartDay) + 7) % 7;
    const weeksToRender2 = 6;
    const weeks = [];
    for (let weekIndex = 0; weekIndex < weeksToRender2; weekIndex++) {
      const weekStartDate = new Date(firstDayInViewMonth);
      weekStartDate.setDate(weekStartDate.getDate() - daysBeforeStart + weekIndex * 7);
      const days = Array.from({ length: 7 }, (_, dayIndex) => {
        const date = new Date(weekStartDate);
        date.setDate(date.getDate() + dayIndex);
        return {
          date,
          isCurrentMonth: date.getMonth() === viewMonth.value.getMonth()
        };
      });
      weeks.push({
        weekNumber: getISOWeekNumber(weekStartDate),
        days
      });
    }
    return weeks;
  });
  const goToDate = (date, preventSelectionUpdate) => {
    focusedDate.value = new Date(date);
    if (focusedDate.value.getFullYear() !== viewMonth.value.getFullYear() || focusedDate.value.getMonth() !== viewMonth.value.getMonth()) {
      viewMonth.value = new Date(focusedDate.value);
    }
    const selectionMode = toValue(options.selectionMode);
    if (!selectionMode || preventSelectionUpdate) return;
    const selection = toValue(options.modelValue);
    switch (selectionMode) {
      case "single":
        options.onUpdateModelValue?.(new Date(date));
        break;
      case "multiple": {
        let values = Array.isArray(selection) ? selection.map((d) => new Date(d)) : [];
        if (isSelected.value(date)) {
          values = values.filter((d) => d.toDateString() !== date.toDateString());
        } else {
          values.push(new Date(date));
        }
        options.onUpdateModelValue?.(values);
        break;
      }
      case "range": {
        const currentRange = typeof selection === "object" && !(selection instanceof Date) && !Array.isArray(selection) ? selection : void 0;
        let newRange;
        if (currentRange?.start && currentRange.end) {
          newRange = { start: new Date(date) };
        } else {
          newRange = {
            start: currentRange?.start ? new Date(currentRange.start) : new Date(date),
            end: currentRange?.start ? new Date(date) : void 0
          };
        }
        newRange = sortDateRange(newRange);
        options.onUpdateModelValue?.(newRange);
        break;
      }
    }
  };
  const handleKeyNavigation = async (event) => {
    let newDate;
    const getNewFocusDateByDiff = (diff, type = "days") => {
      const date = new Date(focusedDate.value);
      if (type === "month") date.setMonth(date.getMonth() + diff);
      else date.setDate(date.getDate() + diff);
      return date;
    };
    switch (event.key) {
      case "ArrowUp":
        newDate = getNewFocusDateByDiff(-7);
        break;
      case "ArrowDown":
        newDate = getNewFocusDateByDiff(7);
        break;
      case "ArrowLeft":
        newDate = getNewFocusDateByDiff(-1);
        break;
      case "ArrowRight":
        newDate = getNewFocusDateByDiff(1);
        break;
      case "Home": {
        const idx = getNormalizedDayIndex(focusedDate.value, toValue(options.weekStartDay));
        newDate = getNewFocusDateByDiff(-idx);
        break;
      }
      case "End": {
        const idx = getNormalizedDayIndex(focusedDate.value, toValue(options.weekStartDay));
        newDate = getNewFocusDateByDiff(6 - idx);
        break;
      }
      case "PageUp":
        newDate = getNewFocusDateByDiff(-(event.shiftKey ? 12 : 1), "month");
        break;
      case "PageDown":
        newDate = getNewFocusDateByDiff(event.shiftKey ? 12 : 1, "month");
        break;
    }
    if (!newDate || isDisabled.value(newDate)) return;
    event.preventDefault();
    goToDate(newDate, true);
    await nextTick();
    tableRef.value?.querySelector(`[data-date="${focusedDate.value.toDateString()}"]`)?.focus();
  };
  const getRangeType = computed(() => {
    return (date, range) => {
      const selection = range ?? toValue(options.modelValue);
      if (!selection || typeof selection !== "object" || Array.isArray(selection) || selection instanceof Date) {
        return;
      }
      const sortedRange = sortDateRange(selection);
      const start = new Date(sortedRange.start);
      start.setHours(0, 0, 0, 0);
      const end = sortedRange.end ? new Date(sortedRange.end) : void 0;
      end?.setHours(23, 59, 59, 999);
      if (date.toDateString() === start.toDateString() && date.toDateString() === end?.toDateString())
        return;
      if (date.toDateString() === start.toDateString()) return "start";
      if (date.toDateString() === end?.toDateString()) return "end";
      if (end && isInDateRange(date, start, end)) return "middle";
    };
  });
  const goToMonthByOffset = (offset) => {
    const date = new Date(viewMonth.value);
    date.setMonth(date.getMonth() + offset, 1);
    viewMonth.value = date;
  };
  const goToToday = () => {
    viewMonth.value = /* @__PURE__ */ new Date();
  };
  const tableRef = createElRef();
  return {
    state: {
      weekdayNames,
      weeksToRender,
      focusedDate,
      viewMonth
    },
    elements: {
      table: {
        role: "grid",
        onKeydown: handleKeyNavigation,
        ref: tableRef
      },
      cell: computed(() => (cell) => ({
        role: "gridcell",
        "aria-selected": isSelected.value(cell.date),
        "aria-disabled": isDisabled.value(cell.date)
      })),
      button: computed(() => (button) => {
        const formatter = new Intl.DateTimeFormat(toValue(options.locale), { dateStyle: "full" });
        const attributes = {
          "aria-label": formatter.format(button.date),
          "data-date": button.date.toDateString()
        };
        const selection = toValue(options.selectionMode);
        if (!selection) return attributes;
        const disabled = isDisabled.value(button.date);
        return {
          ...attributes,
          tabindex: isFocused.value(button.date) && !disabled ? "0" : "-1",
          disabled,
          onClick: disabled ? void 0 : () => goToDate(button.date)
        };
      })
    },
    internals: {
      goToMonthByOffset,
      goToToday,
      isSelected,
      isToday,
      getRangeType,
      isDisabled,
      goToDate,
      isFocused
    }
  };
});
const isSubsetMatching = (subset, target) => Object.entries(subset).every(
  ([key, value]) => target[key] === value
);
const wasKeyPressed = (event, key) => {
  if (typeof key === "string") {
    return event.key === key;
  }
  return isSubsetMatching(
    { altKey: false, ctrlKey: false, metaKey: false, shiftKey: false, ...key },
    event
  );
};
const GRAPHEME_SEGMENTER = new Intl.Segmenter("en-US");
const isPrintableCharacter = (key) => [...GRAPHEME_SEGMENTER.segment(key)].length === 1;
const GLOBAL_LISTENERS = reactive(/* @__PURE__ */ new Map());
const updateRemainingListeners = (type, remaining) => {
  if (remaining?.size) {
    GLOBAL_LISTENERS.set(type, remaining);
    return;
  }
  GLOBAL_LISTENERS.delete(type);
  document.removeEventListener(type, GLOBAL_HANDLER);
};
const removeGlobalListener = (type, listener) => {
  const globalListener = GLOBAL_LISTENERS.get(type);
  globalListener?.delete(listener);
  updateRemainingListeners(type, globalListener);
};
const addGlobalListener = (type, listener) => {
  const globalListener = GLOBAL_LISTENERS.get(type) ?? /* @__PURE__ */ new Set();
  globalListener.add(listener);
  GLOBAL_LISTENERS.set(type, globalListener);
  document.addEventListener(type, GLOBAL_HANDLER);
};
const GLOBAL_HANDLER = (event) => {
  const type = event.type;
  GLOBAL_LISTENERS.get(type)?.forEach((cb) => cb(event));
};
const useGlobalEventListener = ({
  type,
  listener,
  disabled
}) => {
  onBeforeMount(
    () => watchEffect(
      () => disabled?.value ? removeGlobalListener(type, listener) : addGlobalListener(type, listener)
    )
  );
  onBeforeUnmount(() => removeGlobalListener(type, listener));
};
const useOutsideClick = ({
  inside,
  onOutsideClick,
  disabled,
  checkOnTab
}) => {
  const isOutsideClick = (target) => {
    if (!target) return true;
    const raw = toValue(inside);
    const elements = Array.isArray(raw) ? raw : [raw];
    return !elements.some((element) => {
      const nativeEl = getNativeElement(element);
      return nativeEl?.contains(target);
    });
  };
  const clickListener = (event) => {
    if (isOutsideClick(event.target)) onOutsideClick(event);
  };
  useGlobalEventListener({ type: "mousedown", listener: clickListener, disabled });
  if (checkOnTab) {
    const keydownListener = (event) => {
      if (event.key !== "Tab") return;
      if (isOutsideClick(document.activeElement)) {
        onOutsideClick(event);
      }
      const controller = new AbortController();
      const { signal } = controller;
      const onFocusIn = (event2) => {
        const target = event2.target;
        if (isOutsideClick(target)) {
          onOutsideClick(event2);
        }
        controller.abort();
      };
      const onWindowBlur = (event2) => {
        if (isOutsideClick(document.activeElement)) {
          onOutsideClick(event2);
        }
        controller.abort();
      };
      const onKeyUp = (e) => {
        if (e.key === "Tab") controller.abort();
      };
      document.addEventListener("focusin", onFocusIn, {
        once: true,
        capture: true,
        // Capture phase to handle before component focus handlers
        signal
      });
      window.addEventListener("blur", onWindowBlur, { once: true, signal });
      document.addEventListener("keyup", onKeyUp, {
        once: true,
        capture: true,
        // Capture phase to prevent conflicts with prevented keydown
        signal
      });
    };
    useGlobalEventListener({ type: "keydown", listener: keydownListener, disabled });
  }
};
const debounce = (handler, timeout) => {
  let timer;
  const func = (...lastArgs) => {
    clearTimeout(timer);
    timer = setTimeout(() => handler(...lastArgs), toValue(timeout));
  };
  func.abort = () => clearTimeout(timer);
  return func;
};
const useTypeAhead = (callback, timeout = 500) => {
  let inputString = "";
  const debouncedReset = debounce(() => inputString = "", timeout);
  return (event) => {
    if (!isPrintableCharacter(event.key)) {
      return;
    }
    debouncedReset();
    inputString = `${inputString}${event.key}`;
    callback(inputString);
  };
};
const createListbox = createBuilder(
  (options) => {
    const isMultiselect = computed(() => unref(options.multiple) ?? false);
    const isExpanded = computed(() => unref(options.isExpanded) ?? false);
    const descendantKeyIdMap = /* @__PURE__ */ new Map();
    const getOptionId = (value) => {
      if (!descendantKeyIdMap.has(value)) {
        descendantKeyIdMap.set(value, useId());
      }
      return descendantKeyIdMap.get(value);
    };
    const getOptionValueById = (id) => {
      const entries = Array.from(descendantKeyIdMap.entries());
      return entries.find(([_value, key]) => key === id)?.[0];
    };
    const isFocused = ref(false);
    watchEffect(async () => {
      if (!isExpanded.value || options.activeOption.value == void 0 || !isFocused.value && !options.controlled) {
        return;
      }
      const id = getOptionId(options.activeOption.value);
      await nextTick();
      document.getElementById(id)?.scrollIntoView({ block: "nearest", inline: "nearest" });
    });
    const typeAhead = useTypeAhead((inputString) => options.onTypeAhead?.(inputString));
    const handleKeydown = (event) => {
      switch (event.key) {
        case " ":
          event.preventDefault();
          if (options.activeOption.value != void 0) {
            options.onSelect?.(options.activeOption.value);
          }
          break;
        case "ArrowUp":
          event.preventDefault();
          if (options.activeOption.value == void 0) {
            options.onActivateLast?.();
            return;
          }
          options.onActivatePrevious?.(options.activeOption.value);
          break;
        case "ArrowDown":
          event.preventDefault();
          if (options.activeOption.value == void 0) {
            options.onActivateFirst?.();
            return;
          }
          options.onActivateNext?.(options.activeOption.value);
          break;
        case "Home":
          event.preventDefault();
          options.onActivateFirst?.();
          break;
        case "End":
          event.preventDefault();
          options.onActivateLast?.();
          break;
        default:
          typeAhead(event);
      }
    };
    const listbox = computed(
      () => options.controlled ? {
        role: "listbox",
        "aria-multiselectable": isMultiselect.value,
        "aria-label": unref(options.label),
        "aria-description": options.description,
        tabindex: "-1"
      } : {
        role: "listbox",
        "aria-multiselectable": isMultiselect.value,
        "aria-label": unref(options.label),
        "aria-description": options.description,
        tabindex: "0",
        "aria-activedescendant": options.activeOption.value != void 0 ? getOptionId(options.activeOption.value) : void 0,
        onFocus: () => isFocused.value = true,
        onBlur: () => isFocused.value = false,
        onKeydown: handleKeydown
      }
    );
    return {
      elements: {
        listbox,
        group: computed(() => {
          return (options2) => ({
            role: "group",
            "aria-label": options2.label
          });
        }),
        option: computed(() => {
          return (data) => {
            const selected = data.selected ?? false;
            return {
              id: getOptionId(data.value),
              role: "option",
              "aria-label": data.label,
              "aria-disabled": data.disabled,
              "aria-checked": isMultiselect.value ? selected : void 0,
              "aria-selected": !isMultiselect.value ? selected : void 0,
              onClick: () => !data.disabled && options.onSelect?.(data.value)
            };
          };
        })
      },
      state: {
        isFocused
      },
      internals: {
        getOptionId,
        getOptionValueById
      }
    };
  }
);
const OPENING_KEYS = ["ArrowDown", "ArrowUp", " ", "Enter", "Home", "End"];
const CLOSING_KEYS = [
  "Escape",
  { key: "ArrowUp", altKey: true },
  "Enter",
  "Tab"
];
const SELECTING_KEYS = ["Enter"];
const isSelectingKey = (event, withSpace) => {
  const selectingKeys = withSpace ? [...SELECTING_KEYS, " "] : SELECTING_KEYS;
  return isKeyOfGroup(event, selectingKeys);
};
const isKeyOfGroup = (event, group) => group.some((key) => wasKeyPressed(event, key));
const createComboBox = createBuilder(
  ({
    autocomplete: autocompleteRef,
    onAutocomplete,
    onTypeAhead,
    multiple: multipleRef,
    label,
    listLabel,
    listDescription,
    isExpanded: isExpandedRef,
    activeOption,
    onToggle,
    onSelect,
    onActivateFirst,
    onActivateLast,
    onActivateNext,
    onActivatePrevious,
    templateRef
  }) => {
    const controlsId = useId();
    const autocomplete = computed(() => unref(autocompleteRef));
    const isExpanded = computed(() => unref(isExpandedRef));
    const multiple = computed(() => unref(multipleRef));
    const handleInput = (event) => {
      const inputElement = event.target;
      if (autocomplete.value !== "none") {
        onAutocomplete?.(inputElement.value);
      }
    };
    const typeAhead = useTypeAhead((inputString) => onTypeAhead?.(inputString));
    const handleSelect = (value) => {
      onSelect?.(value);
      if (!unref(multiple)) {
        onToggle?.();
      }
    };
    const handleNavigation = (event) => {
      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          if (activeOption.value == void 0) {
            return onActivateLast?.();
          }
          onActivatePrevious?.(activeOption.value);
          break;
        case "ArrowDown":
          event.preventDefault();
          if (activeOption.value == void 0) {
            return onActivateFirst?.();
          }
          onActivateNext?.(activeOption.value);
          break;
        case "Home":
          event.preventDefault();
          onActivateFirst?.();
          break;
        case "End":
          event.preventDefault();
          onActivateLast?.();
          break;
      }
    };
    const handleKeydown = (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
      }
      if (!isExpanded.value && isKeyOfGroup(event, OPENING_KEYS)) {
        onToggle?.();
        if (event.key === " ") {
          event.preventDefault();
        }
        if (event.key === "End") {
          return onActivateLast?.();
        }
        return onActivateFirst?.();
      }
      if (isSelectingKey(event, autocomplete.value === "none")) {
        return handleSelect(activeOption.value);
      }
      if (isExpanded.value && isKeyOfGroup(event, CLOSING_KEYS)) {
        return onToggle?.();
      }
      if (autocomplete.value === "none" && isPrintableCharacter(event.key)) {
        !isExpanded.value && onToggle?.();
        return typeAhead(event);
      }
      if (autocomplete.value !== "none" && isPrintableCharacter(event.key)) {
        !isExpanded.value && onToggle?.();
        return;
      }
      return handleNavigation(event);
    };
    const autocompleteInput = computed(() => {
      if (autocomplete.value === "none") return null;
      return {
        "aria-autocomplete": autocomplete.value,
        type: "text"
      };
    });
    const {
      elements: { option, group, listbox },
      internals: { getOptionId, getOptionValueById }
    } = createListbox({
      label: listLabel,
      description: listDescription,
      multiple,
      controlled: true,
      activeOption,
      isExpanded,
      onSelect: handleSelect
    });
    useOutsideClick({
      inside: templateRef,
      onOutsideClick() {
        if (!isExpanded.value) return;
        onToggle?.(true);
      }
    });
    return {
      elements: {
        option,
        group,
        /**
         * The listbox associated with the combobox.
         */
        listbox: computed(() => ({
          ...listbox.value,
          id: controlsId,
          // preventDefault to not lose focus of the combobox
          onMousedown: (e) => e.preventDefault()
        })),
        /**
         * An input that controls another element, that can dynamically pop-up to help the user set the value of the input.
         * The input MAY be either a single-line text field that supports editing and typing or an element that only displays the current value of the combobox.
         */
        input: computed(() => ({
          role: "combobox",
          "aria-expanded": isExpanded.value,
          "aria-controls": controlsId,
          "aria-label": unref(label),
          "aria-activedescendant": activeOption.value != void 0 ? getOptionId(activeOption.value) : void 0,
          onInput: handleInput,
          onKeydown: handleKeydown,
          ...autocompleteInput.value
        })),
        /**
         * An optional button to control the visibility of the popup.
         */
        button: computed(() => ({
          tabindex: "-1",
          onClick: () => onToggle?.()
        }))
      },
      internals: { getOptionId, getOptionValueById }
    };
  }
);
const useAllSettled = (cb) => {
  const active = ref(false);
  const allPromises = [];
  let latestPromise = Promise.resolve();
  const add = (promise) => {
    active.value = true;
    allPromises.push(promise);
    const newAllSettled = Promise.allSettled(allPromises).then(() => {
      if (newAllSettled === latestPromise) {
        active.value = false;
        allPromises.splice(0, allPromises.length);
      }
    });
    latestPromise = newAllSettled;
  };
  return { add, active };
};
const useLastSettled = (cb) => {
  const active = ref(false);
  let lastId = -1;
  const add = (promise) => {
    const promiseId = ++lastId;
    active.value = true;
    const onFinally = (success) => (resolved) => {
      if (promiseId === lastId) {
        active.value = false;
        if (success) {
          cb(success, resolved);
        } else {
          cb(success);
        }
      }
    };
    promise.then(onFinally(true)).catch(onFinally(false));
  };
  const cancel = () => {
    active.value = false;
    lastId++;
  };
  return { active, add, cancel };
};
const COL_KEY_DATA_ATTR = "data-onyx-col-key";
const COL_INDEX_ARIA_ATTR = "aria-colindex";
const ROW_ID_DATA_ATTR = "data-onyx-row-id";
const ROW_INDEX_ARIA_ATTR = "aria-rowindex";
const StaticResolver = {
  mapCellToIndex: (cell) => Array.from(cell.closest("tr")?.cells ?? []).indexOf(cell),
  mapRowToIndex: (row) => Array.from(row.closest("table")?.rows ?? []).indexOf(row),
  resolveCell: async (cellIndex, rowIndex, table) => table.rows.item(rowIndex)?.cells.item(cellIndex),
  getTotalRows: (table) => table.rows.length,
  getTotalCols: (table) => table.rows.item(0)?.cells.length ?? 0
};
const LazyResolverFactory = ({
  rows,
  cols,
  requestLazyLoad
}) => ({
  mapCellToIndex: (cell) => Number(cell.getAttribute(COL_INDEX_ARIA_ATTR)) - 1,
  mapRowToIndex: (row) => Number(row.getAttribute(ROW_INDEX_ARIA_ATTR)) - 1,
  resolveCell: async (cellIndex, rowIndex, table) => {
    const queryCell = () => table.querySelector(
      `*[${ROW_INDEX_ARIA_ATTR}="${rowIndex + 1}"] *[${COL_INDEX_ARIA_ATTR}="${cellIndex + 1}"]`
    );
    let cell = queryCell();
    if (cell) {
      return cell;
    }
    await requestLazyLoad(cellIndex, rowIndex);
    cell = queryCell();
    if (cell) {
      return cell;
    }
    throw new Error(
      `Table cell with row index "${rowIndex}" and column index "${cellIndex}" was not found after requested lazy loading and is unable to be focused!`
    );
  },
  getTotalRows: () => toValue(rows),
  getTotalCols: () => toValue(cols)
});
const createDataGrid = createBuilder(
  (options) => {
    const tableElement = createElRef();
    const lazy = options.lazy && toRef(options.lazy);
    const busy = computed(() => toValue(options.loading) ?? busySet.active.value);
    const resolver = lazy ? LazyResolverFactory({
      cols: () => lazy.value.totalCols,
      rows: () => lazy.value.totalRows,
      requestLazyLoad: lazy.value.requestLazyLoad
    }) : StaticResolver;
    const labelId = useId();
    const selectedCell = options.selectedCell || ref();
    const selectedCellEl = createElRef();
    const focusQueue = useLastSettled((success, cell) => {
      if (success) {
        cell?.focus();
      }
    });
    const busySet = useAllSettled();
    const findFirstCell = () => tableElement.value?.querySelector?.(
      `[${ROW_ID_DATA_ATTR}] [${COL_KEY_DATA_ATTR}]`
    );
    const setSelected = (element) => {
      const colKey = element.closest(`[${COL_KEY_DATA_ATTR}]`)?.getAttribute(COL_KEY_DATA_ATTR);
      const rowId = element.closest(`[${ROW_ID_DATA_ATTR}]`)?.getAttribute(ROW_ID_DATA_ATTR);
      if (colKey && rowId) {
        selectedCell.value = {
          rowId,
          colKey
        };
      }
    };
    const ensureTabTarget = () => {
      if (selectedCell.value && selectedCellEl.value?.isConnected) {
        return;
      }
      const firstCell = findFirstCell();
      if (firstCell) {
        setSelected(firstCell);
      }
    };
    let mutationObserver;
    onMounted(() => {
      ensureTabTarget();
      mutationObserver = new MutationObserver(ensureTabTarget);
      watch(
        tableElement,
        () => {
          if (!tableElement.value) {
            return;
          }
          mutationObserver?.disconnect();
          mutationObserver?.observe(tableElement.value, {
            childList: true,
            attributes: true,
            subtree: true,
            attributeFilter: ["value"]
          });
        },
        { immediate: true }
      );
    });
    onBeforeUnmount(() => {
      mutationObserver?.disconnect();
    });
    const onFocusin = (event) => {
      setSelected(event.target);
      focusQueue.cancel();
    };
    const onKeydown = (event) => {
      const target = event.target;
      const cellElement = target.closest("td, th");
      const rowElement = target.closest("tr");
      const tableElement2 = target.closest("table");
      if (!cellElement || !rowElement || !tableElement2) {
        return;
      }
      const { getTotalRows, getTotalCols, mapRowToIndex, mapCellToIndex, resolveCell } = resolver;
      const colIndex = mapCellToIndex(cellElement);
      const rowIndex = mapRowToIndex(rowElement);
      const totalRows = getTotalRows(tableElement2);
      const totalCols = getTotalCols(tableElement2);
      let newColIndex = colIndex;
      let newRowIndex = rowIndex;
      if (wasKeyPressed(event, { ctrlKey: true, key: "Home" })) {
        newColIndex = 0;
        newRowIndex = 0;
      } else if (wasKeyPressed(event, { ctrlKey: true, key: "End" })) {
        newColIndex = totalCols === "unknown" ? Infinity : totalCols - 1;
        newRowIndex = totalRows === "unknown" ? Infinity : totalRows - 1;
      } else if (wasKeyPressed(event, "ArrowUp")) {
        newRowIndex = rowIndex - 1;
      } else if (wasKeyPressed(event, "ArrowDown")) {
        newRowIndex = rowIndex + 1;
      } else if (wasKeyPressed(event, "ArrowLeft")) {
        newColIndex = colIndex - 1;
      } else if (wasKeyPressed(event, "ArrowRight")) {
        newColIndex = colIndex + 1;
      } else if (wasKeyPressed(event, "Home")) {
        newColIndex = 0;
      } else if (wasKeyPressed(event, "End")) {
        newColIndex = totalCols === "unknown" ? Infinity : totalCols - 1;
      } else {
        return;
      }
      event.preventDefault();
      const maxRows = totalRows === "unknown" ? Infinity : totalRows - 1;
      newRowIndex = Math.max(Math.min(newRowIndex, maxRows), 0);
      const maxCols = totalCols === "unknown" ? Infinity : totalCols - 1;
      newColIndex = Math.max(Math.min(newColIndex, maxCols), 0);
      (async () => {
        const promiseResolveCell = resolveCell(newColIndex, newRowIndex, tableElement2);
        focusQueue.add(promiseResolveCell);
        busySet.add(promiseResolveCell);
      })();
    };
    return {
      elements: {
        label: {
          id: labelId
        },
        table: computed(
          () => ({
            ref: tableElement,
            onFocusin,
            onKeydown,
            role: "grid",
            "aria-busy": busy.value,
            "aria-labelledby": labelId,
            "aria-rowcount": lazy?.value.totalRows === "unknown" ? -1 : lazy?.value.totalRows,
            "aria-colcount": lazy?.value.totalCols === "unknown" ? -1 : lazy?.value.totalCols
          })
        ),
        tr: ({ rowId, rowIndex }) => ({
          [ROW_ID_DATA_ATTR]: rowId.toString(),
          "aria-rowindex": rowIndex == void 0 ? void 0 : rowIndex + 1,
          role: "row"
        }),
        td: computed(() => ({ rowId, colKey, colIndex }) => {
          const isSelected = colKey.toString() === selectedCell.value?.colKey.toString() && rowId.toString() === selectedCell.value?.rowId.toString();
          return {
            tabindex: isSelected ? "0" : "-1",
            ref: isSelected ? selectedCellEl : void 0,
            [COL_KEY_DATA_ATTR]: colKey.toString(),
            // TODO: handle symbols
            "aria-colindex": colIndex == void 0 ? void 0 : colIndex + 1,
            role: "cell"
          };
        })
      },
      state: {
        /**
         * Indicates that the data grid expects a content change soon, e.g. because more or other data is loaded.
         * If `loading` is passed in via the options, this will mirror its value.
         * Otherwise it will be dynamically set based on the running state of the `requestLazyLoad` promises.
         */
        busy
      },
      internals: {}
    };
  }
);
const createMenuButton = createBuilder((options) => {
  const rootId = useId();
  const menuId = useId();
  const rootRef = createElRef();
  const menuRef = createElRef();
  const buttonId = useId();
  const position = computed(() => toValue(options.position) ?? "bottom");
  useGlobalEventListener({
    type: "keydown",
    listener: (e) => e.key === "Escape" && setExpanded(false),
    disabled: computed(() => !options.isExpanded.value)
  });
  const updateDebouncedExpanded = debounce(() => options.onToggle(), 200);
  watch(options.isExpanded, () => updateDebouncedExpanded.abort());
  const setExpanded = (expanded, debounced = false) => {
    if (options.disabled?.value) return;
    if (expanded === options.isExpanded.value) {
      updateDebouncedExpanded.abort();
      return;
    }
    if (debounced) {
      updateDebouncedExpanded();
      return;
    }
    options.onToggle();
  };
  const focusRelativeItem = (next) => {
    const currentMenuItem = document.activeElement;
    const currentMenu = currentMenuItem?.closest('[role="menu"]') || menuRef.value;
    if (!currentMenu) return;
    const menuItems = Array.from(currentMenu.querySelectorAll('[role="menuitem"]')).filter((item) => item.closest('[role="menu"]') === currentMenu);
    if (position.value === "top") menuItems.reverse();
    let nextIndex = 0;
    if (currentMenuItem) {
      const currentIndex = menuItems.indexOf(currentMenuItem);
      switch (next) {
        case "next":
          nextIndex = currentIndex + 1;
          break;
        case "prev":
          nextIndex = currentIndex - 1;
          break;
        case "first":
          nextIndex = 0;
          break;
        case "last":
          nextIndex = menuItems.length - 1;
          break;
      }
    }
    const nextMenuItem = menuItems[nextIndex];
    nextMenuItem?.focus();
  };
  const handleKeydown = (event) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        focusRelativeItem(position.value === "bottom" ? "next" : "prev");
        break;
      case "ArrowUp":
        event.preventDefault();
        focusRelativeItem(position.value === "bottom" ? "prev" : "next");
        break;
      case "Home":
        event.preventDefault();
        focusRelativeItem("first");
        break;
      case "End":
        event.preventDefault();
        focusRelativeItem("last");
        break;
      case " ":
      case "Enter":
        if (event.target instanceof HTMLInputElement) break;
        event.preventDefault();
        event.target.click();
        break;
      case "Escape":
        event.preventDefault();
        setExpanded(false);
        break;
    }
  };
  const triggerEvents = computed(() => {
    if (toValue(options.trigger) !== "hover") return;
    return {
      onMouseenter: () => setExpanded(true),
      onMouseleave: () => setExpanded(false, true)
    };
  });
  useOutsideClick({
    inside: rootRef,
    onOutsideClick: () => setExpanded(false),
    disabled: computed(() => !options.isExpanded.value),
    checkOnTab: true
  });
  return {
    elements: {
      root: computed(() => ({
        id: rootId,
        onKeydown: handleKeydown,
        ref: rootRef,
        ...triggerEvents.value
      })),
      button: computed(
        () => ({
          "aria-controls": menuId,
          "aria-expanded": options.isExpanded.value,
          "aria-haspopup": true,
          onFocus: () => setExpanded(true, true),
          onClick: () => toValue(options.trigger) == "click" ? setExpanded(!options.isExpanded.value) : void 0,
          id: buttonId,
          disabled: options.disabled?.value
        })
      ),
      menu: {
        id: menuId,
        ref: menuRef,
        role: "menu",
        "aria-labelledby": buttonId,
        onClick: () => setExpanded(false)
      },
      ...createMenuItems().elements
    }
  };
});
const createMenuItems = createBuilder((options) => {
  const onKeydown = (event) => {
    switch (event.key) {
      case "ArrowRight":
      case " ":
      case "Enter":
        event.preventDefault();
        options?.onOpen?.();
        break;
    }
  };
  return {
    elements: {
      listItem: {
        role: "none"
      },
      menuItem: (data) => ({
        "aria-current": data.active ? "page" : void 0,
        "aria-disabled": data.disabled,
        role: "menuitem",
        onKeydown
      })
    }
  };
});
const MathUtils = {
  /**
   * Ensures that a given `number` is or is between a given `min` and `max`.
   */
  clamp: (number, min, max) => Math.max(Math.min(number, max), min),
  /**
   * Returns the count of decimal places in a number.
   * @param number - The number to check.
   * @returns The count of decimal places.
   *
   * decimals(1.23); // 2
   * decimals(10);   // 0
   */
  decimalsCount: (number) => String(number).split(".")[1]?.length ?? 0,
  /**
   *  Converts a value within a range to a percentage (0-100).
   *
   * @param value - The value to convert.
   * @param min - The minimum allowed value.
   * @param max - The maximum allowed value.
   * @returns The percentage representation of the value.
   */
  valueToPercent: (value, min, max) => (value - min) * 100 / (max - min),
  /**
   * Converts a percentage (0-100) to a value within a range.
   *
   * @param percent - The percentage to convert.
   * @param min - The minimum allowed value.
   * @param max - The maximum allowed value.
   * @returns The value representation of the percentage.
   */
  percentToValue: (percent, min, max) => (max - min) * percent + min
};
const createNavigationMenu = createBuilder(({ navigationName }) => {
  const navId = useId();
  const getMenuButtons = () => {
    const nav = navId ? document.getElementById(navId) : void 0;
    if (!nav) return [];
    return Array.from(nav.querySelectorAll("button[aria-expanded][aria-controls]"));
  };
  const focusRelative = (trigger, next) => {
    const menuButtons = getMenuButtons();
    const index = menuButtons.indexOf(trigger);
    if (index === -1) return;
    const nextIndex = MathUtils.clamp(
      index + (next === "next" ? 1 : -1),
      0,
      menuButtons.length - 1
    );
    menuButtons[nextIndex]?.focus();
  };
  return {
    elements: {
      nav: {
        "aria-label": unref(navigationName),
        id: navId,
        onKeydown: (event) => {
          switch (event.key) {
            case "ArrowRight":
              focusRelative(event.target, "next");
              break;
            case "ArrowLeft":
              focusRelative(event.target, "previous");
              break;
          }
        }
      }
    }
  };
});
const areArraysEqual = (arrayA, arrayB, comparer = (a, b) => a === b) => arrayA.length === arrayB.length && arrayA.every((value, index) => comparer(value, arrayB[index]));
const NAVIGATION_KEYS = /* @__PURE__ */ new Set([
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "PageUp",
  "PageDown",
  "Home",
  "End"
]);
const INCREMENT_KEYS = /* @__PURE__ */ new Set(["ArrowRight", "ArrowUp", "PageUp"]);
const DECREMENT_KEYS = /* @__PURE__ */ new Set(["ArrowLeft", "ArrowDown", "PageDown"]);
const createSlider = createBuilder(
  (options) => {
    const sliderRef = createElRef();
    const firstThumbRef = createElRef();
    const secondThumbRef = createElRef();
    const min = computed(() => toValue(options.min) ?? 0);
    const max = computed(() => toValue(options.max) ?? 100);
    const step = computed(() => toValue(options.step) ?? 1);
    const draggingThumbIndex = ref();
    watch(draggingThumbIndex, (newThumbIndex) => {
      if (newThumbIndex == void 0) return;
      focusThumb(newThumbIndex);
    });
    const focusThumb = (index) => {
      if (index === 0) firstThumbRef.value?.focus();
      else if (index === 1) secondThumbRef.value?.focus();
    };
    const shiftStep = computed(() => {
      const shiftStep2 = toValue(options.shiftStep);
      if (shiftStep2 != void 0) return shiftStep2;
      const stepMultiple = Math.max(1, Math.round((max.value - min.value) * 0.1 / step.value));
      return stepMultiple * step.value;
    });
    const getNormalizedValue = computed(() => {
      return (value) => {
        let values = typeof value === "number" ? [value] : value;
        values = values.map((value2) => MathUtils.clamp(value2, min.value, max.value)).map((value2) => {
          const stepDecimals = MathUtils.decimalsCount(step.value);
          return Number(
            (Math.round((value2 - min.value) / step.value) * step.value + min.value).toFixed(
              stepDecimals
            )
          );
        }).sort((a, b) => a - b);
        return values;
      };
    });
    const normalizedValue = computed(
      () => getNormalizedValue.value(toValue(options.value))
    );
    const updateValue = (value, index) => {
      const currentValue = normalizedValue.value.slice();
      const otherIndex = index === 0 ? 1 : 0;
      const otherValue = currentValue[otherIndex];
      if (otherValue != void 0) {
        if (index < otherIndex && value > otherValue) {
          value = otherValue;
        } else if (index > otherIndex && value < otherValue) {
          value = otherValue;
        }
      }
      currentValue[index] = value;
      const normalized = getNormalizedValue.value(currentValue);
      const newValue = normalized.length > 1 ? normalized : normalized[0];
      if (areArraysEqual(normalized, normalizedValue.value)) return;
      options.onChange?.(newValue);
    };
    const updateValueByStep = (direction) => {
      const index = 0;
      const stepValue = direction === "increase" ? step.value : -step.value;
      const currentValue = normalizedValue.value[index];
      updateValue(currentValue + stepValue, index);
      focusThumb(index);
    };
    const getValueInPercentage = computed(() => {
      return (value) => {
        const percentage = MathUtils.valueToPercent(value, min.value, max.value);
        return MathUtils.clamp(percentage, 0, 100);
      };
    });
    const handleKeydown = (event, thumbIndex) => {
      if (!NAVIGATION_KEYS.has(event.key)) return;
      event.preventDefault();
      const currentValue = normalizedValue.value.slice();
      if (currentValue[thumbIndex] == void 0) return;
      const stepSize = event.shiftKey ? shiftStep.value : step.value;
      if (event.key === "Home") {
        return updateValue(min.value, thumbIndex);
      }
      if (event.key === "End") {
        return updateValue(max.value, thumbIndex);
      }
      if (INCREMENT_KEYS.has(event.key)) {
        updateValue(currentValue[thumbIndex] + stepSize, thumbIndex);
      } else if (DECREMENT_KEYS.has(event.key)) {
        updateValue(currentValue[thumbIndex] - stepSize, thumbIndex);
      }
    };
    const handlePointerdown = (event) => {
      event.preventDefault();
      const value = getValueFromCoordinates(event.x);
      if (value == void 0) return;
      const thumb = normalizedValue.value.reduce(
        (previous, thumbValue, index) => {
          const distance = Math.abs(thumbValue - value);
          if (distance < previous.distance) return { index, distance };
          return previous;
        },
        { index: 0, distance: Number.POSITIVE_INFINITY }
      );
      updateValue(value, thumb.index);
      draggingThumbIndex.value = thumb.index;
    };
    const handlePointermove = (event) => {
      if (draggingThumbIndex.value == void 0) return;
      const value = getValueFromCoordinates(event.x);
      if (value == void 0) return;
      updateValue(value, draggingThumbIndex.value);
    };
    useGlobalEventListener({
      type: "pointermove",
      listener: handlePointermove,
      disabled: computed(() => draggingThumbIndex.value == void 0)
    });
    const handlePointerup = () => {
      draggingThumbIndex.value = void 0;
    };
    useGlobalEventListener({
      type: "pointerup",
      listener: handlePointerup,
      disabled: computed(() => draggingThumbIndex.value == void 0)
    });
    const getValueFromCoordinates = (x) => {
      const rect = sliderRef.value?.getBoundingClientRect();
      if (!rect || rect.width <= 0) return;
      const percent = MathUtils.clamp((x - rect.left) / rect.width, 0, 1);
      return MathUtils.percentToValue(percent, min.value, max.value);
    };
    return {
      elements: {
        /**
         * Root slider container element
         */
        root: computed(() => {
          const events = {
            onPointerdown: handlePointerdown
          };
          return {
            ref: sliderRef,
            style: { touchAction: "pan-y" },
            ...toValue(options.disabled) ? void 0 : events
          };
        }),
        /**
         * Input inside each thumb for accessibility
         */
        thumbInput: computed(() => (data) => {
          const events = {
            onKeydown: (event) => handleKeydown(event, data.index)
          };
          return {
            min: min.value,
            max: max.value,
            value: data.value,
            role: "slider",
            type: "range",
            "aria-label": toValue(options.label),
            "aria-valuemin": min.value,
            "aria-valuemax": max.value,
            "aria-valuenow": data.value,
            "aria-orientation": "horizontal",
            step: step.value,
            disabled: toValue(options.disabled),
            ref: data.index === 0 ? firstThumbRef : data.index === 1 ? secondThumbRef : void 0,
            ...toValue(options.disabled) ? void 0 : events
          };
        })
      },
      state: {
        normalizedValue,
        shiftStep,
        /**
         * Track element representing the selected range
         */
        track: computed(() => {
          const isRange = normalizedValue.value.length > 1;
          const start = isRange ? normalizedValue.value[0] : min.value;
          const startPercentage = getValueInPercentage.value(start);
          const end = normalizedValue.value.at(-1);
          const endPercentage = getValueInPercentage.value(end);
          return {
            start,
            startPercentage,
            end,
            endPercentage
          };
        })
      },
      internals: {
        getValueInPercentage,
        updateValue,
        updateValueByStep
      }
    };
  }
);
const createTabs = createBuilder((options) => {
  const idMap = /* @__PURE__ */ new Map();
  const getId = (value) => {
    if (!idMap.has(value)) {
      idMap.set(value, { tabId: useId(), panelId: useId() });
    }
    return idMap.get(value);
  };
  const handleKeydown = (event) => {
    const tab = event.target;
    const enabledTabs = Array.from(
      tab.parentElement?.querySelectorAll('[role="tab"]') ?? []
    ).filter((tab2) => tab2.ariaDisabled !== "true");
    const currentTabIndex = enabledTabs.indexOf(tab);
    const focusElement = (element) => {
      if (element instanceof HTMLElement) element.focus();
    };
    const focusFirstTab = () => focusElement(enabledTabs.at(0));
    const focusLastTab = () => focusElement(enabledTabs.at(-1));
    const focusTab = (direction) => {
      if (currentTabIndex === -1) return;
      const newIndex = direction === "next" ? currentTabIndex + 1 : currentTabIndex - 1;
      if (newIndex < 0) {
        return focusLastTab();
      } else if (newIndex >= enabledTabs.length) {
        return focusFirstTab();
      }
      return focusElement(enabledTabs.at(newIndex));
    };
    switch (event.key) {
      case "ArrowRight":
        focusTab("next");
        break;
      case "ArrowLeft":
        focusTab("previous");
        break;
      case "Home":
        focusFirstTab();
        break;
      case "End":
        focusLastTab();
        break;
      case "Enter":
      case " ":
        {
          const tabEntry = Array.from(idMap.entries()).find(([, { tabId }]) => tabId === tab.id);
          if (tabEntry) options.onSelect?.(tabEntry[0]);
        }
        break;
    }
  };
  return {
    elements: {
      tablist: computed(() => ({
        role: "tablist",
        "aria-label": unref(options.label),
        onKeydown: handleKeydown
      })),
      tab: computed(() => {
        return (data) => {
          const { tabId: selectedTabId } = getId(unref(options.selectedTab));
          const { tabId, panelId } = getId(data.value);
          const isSelected = tabId === selectedTabId;
          return {
            id: tabId,
            role: "tab",
            "aria-selected": isSelected,
            "aria-controls": panelId,
            "aria-disabled": data.disabled ? true : void 0,
            onClick: () => options.onSelect?.(data.value),
            tabindex: isSelected && !data.disabled ? 0 : -1
          };
        };
      }),
      tabpanel: computed(() => {
        return (data) => {
          const { tabId, panelId } = getId(data.value);
          return {
            id: panelId,
            role: "tabpanel",
            "aria-labelledby": tabId
          };
        };
      })
    }
  };
});
const createToggleButton = createBuilder((options) => {
  return {
    elements: {
      /**
       * A html button element that is supposed to act as a toggle button.
       */
      button: computed(
        () => ({
          "aria-pressed": toValue(options.isPressed),
          onClick: options.onToggle
        })
      )
    }
  };
});
const useDismissible = ({ isExpanded }) => useGlobalEventListener({
  type: "keydown",
  listener: (e) => {
    if (e.key === "Escape") {
      isExpanded.value = false;
    }
  },
  disabled: computed(() => !isExpanded.value)
});
const createToggletip = createBuilder(
  ({ toggleLabel, isVisible }) => {
    const triggerId = useId();
    const _isVisible = toRef(isVisible ?? false);
    useDismissible({ isExpanded: _isVisible });
    const toggle = () => _isVisible.value = !_isVisible.value;
    return {
      elements: {
        /**
         * The element which controls the toggletip visibility:
         * Preferably a `button` element.
         */
        trigger: computed(() => ({
          id: triggerId,
          onClick: toggle,
          "aria-label": toValue(toggleLabel)
        })),
        /**
         * The element with the relevant toggletip content.
         * Only simple, textual content is allowed.
         */
        tooltip: {
          onToggle: (e) => {
            const tooltip = e.target;
            _isVisible.value = tooltip.matches(":popover-open");
          },
          anchor: triggerId,
          popover: "auto",
          role: "status",
          tabindex: "-1"
        }
      },
      state: {
        isVisible: _isVisible
      }
    };
  }
);
const createTooltip = createBuilder(({ debounce: debounce2, isVisible }) => {
  const tooltipId = useId();
  const _isVisible = toRef(isVisible ?? false);
  let timeout;
  const debouncedVisible = computed({
    get: () => _isVisible.value,
    set: (newValue) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        _isVisible.value = newValue;
      }, toValue(debounce2));
    }
  });
  const hoverEvents = {
    onMouseover: () => debouncedVisible.value = true,
    onMouseout: () => debouncedVisible.value = false,
    onFocusin: () => _isVisible.value = true,
    onFocusout: () => _isVisible.value = false
  };
  useDismissible({ isExpanded: _isVisible });
  return {
    elements: {
      /**
       * The element which controls the tooltip visibility on hover.
       */
      trigger: {
        "aria-describedby": tooltipId,
        ...hoverEvents
      },
      /**
       * The element describing the tooltip.
       * Only simple, textual and non-focusable content is allowed.
       */
      tooltip: {
        popover: "manual",
        role: "tooltip",
        id: tooltipId,
        tabindex: "-1",
        ...hoverEvents
      }
    },
    state: {
      isVisible: _isVisible
    }
  };
});
export {
  CLOSING_KEYS,
  OPENING_KEYS,
  _unstableCreateCalendar,
  createBuilder,
  createComboBox,
  createDataGrid,
  createElRef,
  createListbox,
  createMenuButton,
  createMenuItems,
  createNavigationMenu,
  createSlider,
  createTabs,
  createToggleButton,
  createToggletip,
  createTooltip,
  debounce,
  getNativeElement,
  isPrintableCharacter,
  useGlobalEventListener,
  useOutsideClick,
  wasKeyPressed
};
